import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import lodash from 'lodash';
import Flicking from '@egjs/react-flicking';

import { COLORS } from 'shared/constants';
import { DateType, UiControlPropsType } from 'shared/types';
import { toDate } from 'shared/utilities';
import { useObservable, useReaction } from 'shared/hooks';

import { UiIcon } from '../UiIcon';
import { UiSelect } from '../UiSelect';
import { UiButton } from '../UiButton';

import './styles.scss';
import '@egjs/react-flicking/dist/flicking.css';

type PropsType = UiControlPropsType<string | null> & {
    items?: string[];
    minDate?: string;
    withTime?: boolean;
    dateTimeMode?: 'preserve' | 'nextHour';
    hideTimeInputs?: boolean;
    defaultValue?: string;
    slots?: {
        header?: () => React.ReactNode;
        day?: (options: { className: string; date: DateType; isOuter: boolean; onClick: () => void }) => React.ReactNode;
    },
    disablesDays?: number[] | null;
    onUpdateDate?: (date: DateType) => void;
};
export const UiCalendar = observer(function UiCalendar({
    value,
    onChange = () => undefined,
    className,
    name = 'date',
    style,
    items = [],
    minDate,
    slots,
    defaultValue,
    withTime,
    dateTimeMode = 'preserve',
    hideTimeInputs = false,
    disablesDays,
    onUpdateDate = () => undefined
}: PropsType) {
    const store = useObservable({
        date: toDate(value || defaultValue),
        hour: 12,
        minute: 0,
        draftHour: 12,
        draftMinute: 0,
        isTimeOpened: false,
        isTimeTouched: false,
    });
    const now = useMemo(() => toDate(), []);
    const date = useMemo(() => toDate(value), [value]);
    const minimumDate = useMemo(() => minDate ? toDate(minDate).startOf('day') : null, [minDate]);

    useReaction(() => {
        onUpdateDate(store.date)
    }, () => store.date)

    useEffect(() => {
        const date = toDate(value || defaultValue);
        store.set('date', date);
        store.set('hour', date.hour());
        store.set('minute', date.minute());
        store.set('draftHour', date.hour());
        store.set('draftMinute', date.minute());
    }, [value, store, defaultValue]);

    const days = useMemo(() => {
        const days: DateType[] = [];
        let current = store.date.startOf('month').startOf('week');
        const end = store.date.endOf('month').endOf('week');
        while (current.isBefore(end)) {
            days.push(current);
            current = current.add(1, 'day');
        }
        return days;
    }, [store.date]);

    const years = useMemo(() => {
        const years: { id: number; name: string }[] = [];
        let current = now.startOf('year').subtract(5, 'years');
        const end = store.date.startOf('year').add(2, 'years');
        while (current.isBefore(end)) {
            years.push({
                id: current.year(),
                name: current.format('YYYY'),
            });
            current = current.add(1, 'year');
        }
        return years;
    }, [now, store.date]);

    const months = useMemo(() => {
        const months: { id: number; name: string }[] = [];
        let current = store.date.startOf('year').startOf('month');
        const end = store.date.endOf('year').endOf('month');
        while (current.isBefore(end)) {
            months.push({
                id: current.month(),
                name: lodash.capitalize(current.format('MMMM')),
            });
            current = current.add(1, 'month');
        }
        return months;
    }, [store.date]);

    const itemsDates = useMemo(() => items.map(item => toDate(item)), [items]);

    const handleChange = (date: DateType, event?: Event) => {
        onChange({
            name,
            value: date.format(),
        }, event);
    };

    const getDateWithTime = (day: DateType) => {
        if (!withTime) {
            return day;
        }

        const time = dateTimeMode === 'nextHour' && !store.isTimeTouched
            ? toDate().add(1, 'hour')
            : toDate().set('hour', store.hour).set('minute', store.minute);

        return day
            .set('hour', time.hour())
            .set('minute', time.minute())
            .set('second', 0)
            .set('millisecond', 0);
    };

    const isDayDisabled = (day: DateType) => (
        (!!minimumDate && day.isBefore(minimumDate, 'day'))
        || !!disablesDays?.includes(day.day())
    );

    const handleDayClick = (day: DateType) => {
        if (isDayDisabled(day)) {
            return;
        }

        handleChange(getDateWithTime(day));
    };

    const hours = useMemo(() => Array.from({ length: 24 }, (value, index) => index), []);
    const minutes = useMemo(() => Array.from({ length: 60 }, (value, index) => index), []);

    return (
        <div
            className={classnames('ui-new-calendar', className)}
            style={style}
        >
            {!!slots?.header && slots.header()}
            {!slots?.header && !store.isTimeOpened && (
                <div className="ui-new-calendar-header">
                    <div className="ui-new-calendar-header__button" onClick={() => store.set('date', store.date.subtract(1, 'month'))}>
                        <UiIcon.ChevronLeft size={16} color={COLORS._2C2F35} />
                    </div>
                    <div className="ui-new-calendar-header__title">
                        <UiSelect
                            slots={{
                                control: ({ onClick }) => <span onClick={onClick}>{store.date.format('MMMM')}</span>,
                            }}
                            items={months}
                            value={store.date.month()}
                            onChange={({ value }) => {
                                if (value !== null) {
                                    store.set('date', store.date.set('month', value));
                                }
                            }}
                        />
                        <UiSelect
                            slots={{
                                control: ({ onClick }) => <span onClick={onClick}>{store.date.format('YYYY')}</span>,
                            }}
                            items={years}
                            value={store.date.year()}
                            onChange={({ value }) => {
                                if (value !== null) {
                                    store.set('date', store.date.set('year', value));
                                }
                            }}
                        />
                    </div>
                    <div className="ui-new-calendar-header__button" onClick={() => store.set('date', store.date.add(1, 'month'))}>
                        <UiIcon.ChevronRight size={16} color={COLORS._2C2F35} />
                    </div>
                </div>
            )}
            <div className="ui-new-calendar-control">
                <div className="ui-new-calendar-control__header">
                    <span>Пн</span>
                    <span>Вт</span>
                    <span>Ср</span>
                    <span>Чт</span>
                    <span>Пт</span>
                    <span>Сб</span>
                    <span>Вс</span>
                </div>
                <div className="ui-new-calendar-control__days">
                    {days.map(day => {
                        const isDisabled = isDayDisabled(day);
                        const className = classnames('ui-new-calendar-control__day', {
                            'ui-new-calendar-control__day--selected': day.isSame(date, 'day'),
                            'ui-new-calendar-control__day--today': day.isSame(now, 'day'),
                            'ui-new-calendar-control__day--marked': itemsDates.some(itemDate => itemDate.isSame(day, 'day')),
                            'ui-new-calendar-control__day--weekend': day.day() === 6 || day.day() === 0,
                            'ui-new-calendar-control__day--outer': !day.isSame(store.date, 'month'),
                            'ui-new-calendar-control__day--disabled': isDisabled,
                        });
                        if (slots?.day) {
                            return slots.day({
                                className,
                                isOuter: !day.isSame(store.date, 'month'),
                                date: day,
                                onClick: () => handleDayClick(day),
                            });
                        }
                        return (
                            <div key={day.format()} className={className} onClick={() => handleDayClick(day)}>
                                <span>{day.format('D')}</span>
                            </div>
                        );
                    })}
                </div>
                {store.isTimeOpened && (
                    <div className="ui-new-calendar-time-outer">
                        <div className="ui-new-calendar-time-outer__columns">
                            <Flicking
                                preventClickOnDrag
                                preventDefaultOnDrag
                                className="ui-new-calendar-time-outer-column"
                                useFindDOMNode
                                horizontal={false}
                                align="center"
                                panelsPerView={3}
                                defaultIndex={store.draftHour}
                                circular={true}
                                autoInit
                                onChanged={(e) => {
                                    store.set('draftHour', e.index);
                                }}
                            >
                                {hours.map(hour => (
                                    <div
                                        key={hour}
                                        className={classnames('ui-new-calendar-time-outer-column__item flicking-panel', {
                                            'ui-new-calendar-time-outer-column__item--active': hour === store.draftHour,
                                        })}
                                    >
                                        {hour}
                                    </div>
                                ))}
                            </Flicking>
                            <Flicking
                                preventClickOnDrag
                                preventDefaultOnDrag
                                className="ui-new-calendar-time-outer-column"
                                horizontal={false}
                                align="center"
                                panelsPerView={3}
                                circular={true}
                                defaultIndex={store.draftMinute}
                                autoInit
                                onChanged={(e) => {
                                    store.set('draftMinute', e.index);
                                }}
                            >
                                {minutes.map(minute => (
                                    <div
                                        key={minute}
                                        className={classnames('ui-new-calendar-time-outer-column__item flicking-panel', {
                                            'ui-new-calendar-time-outer-column__item--active': minute === store.draftMinute,
                                        })}
                                    >
                                        {minute}
                                    </div>
                                ))}
                            </Flicking>
                        </div>
                        <div className="ui-new-calendar-time-outer__actions">
                            <UiButton
                                onClick={() => {
                                    store.set('draftHour', store.hour);
                                    store.set('draftMinute', store.minute);
                                    store.set('isTimeOpened', false);
                                }}
                                style={{
                                    backgroundColor: [COLORS._EAECF1, COLORS._C6CAD1],
                                    color: [COLORS._2C2F35, COLORS._2C2F35],
                                }}
                            >
                                Отмена
                            </UiButton>
                            <UiButton
                                onClick={() => {
                                    store.set('hour', store.draftHour);
                                    store.set('minute', store.draftMinute);
                                    store.set('isTimeOpened', false);
                                    store.set('isTimeTouched', true);
                                    handleChange(date.clone().set('hour', store.draftHour).set('minute', store.draftMinute));
                                }}
                            >
                                Готово
                            </UiButton>
                        </div>
                    </div>
                )}
            </div>
            {withTime && (!store.isTimeOpened || !hideTimeInputs) && (
                <div className="ui-new-calendar-time">
                    <div
                        className={classnames('ui-new-calendar-time-control', {
                            'ui-new-calendar-time-control--active': store.isTimeOpened,
                        })}
                        onClick={() => store.set('isTimeOpened', true)}
                    >
                        <span>{store.hour}</span>
                        <div className="ui-new-calendar-time-control__icons">
                            <UiIcon.ChevronUp
                                size={16}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    store.set('isTimeTouched', true);
                                    handleChange(date.clone().add(1, 'hour'), new Event('datetime'));
                                }}
                            />
                            <UiIcon.ChevronDown
                                size={16}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    store.set('isTimeTouched', true);
                                    handleChange(date.clone().subtract(1, 'hour'), new Event('datetime'));
                                }}
                            />
                        </div>
                    </div>
                    <div
                        className={classnames('ui-new-calendar-time-control', {
                            'ui-new-calendar-time-control--active': store.isTimeOpened,
                        })}
                        onClick={() => store.set('isTimeOpened', true)}
                    >
                        <span>{store.minute}</span>
                        <div className="ui-new-calendar-time-control__icons">
                            <UiIcon.ChevronUp
                                size={16}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    store.set('isTimeTouched', true);
                                    handleChange(date.clone().add(1, 'minute'), new Event('datetime'));
                                }}
                            />
                            <UiIcon.ChevronDown
                                size={16}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    store.set('isTimeTouched', true);
                                    handleChange(date.clone().subtract(1, 'minute'), new Event('datetime'));
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});
